import axios from "../fetch/axiosInstance";
import { useSelector } from "react-redux";
import { useState } from "react";
import { socketId } from "../data/socketID";
import { UPLOAD_RECORD } from "../fetch/apiConfig";
import { uploadRecordFileWithFallback } from "../common/uploadToS3";

//녹화 상태를 담을 배열
let recorder_screen;

const RECORD_UPLOAD_REGISTER_RETRY_COUNT = 3;
const RECORD_UPLOAD_REGISTER_RETRY_DELAY_MS = 1000;
const RECORD_UPLOAD_FAILURE_LOG_KEY = "recordUploadFailureLogs";

const pad = (value) => String(value).padStart(2, "0");

const getRecordTimestamp = () => {
  const date = new Date();

  return [
    date.getFullYear(),
    pad(date.getMonth() + 1),
    pad(date.getDate()),
    `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(
      date.getSeconds(),
    )}`,
  ].join("_");
};

const wait = (delay) =>
  new Promise((resolve) => {
    setTimeout(resolve, delay);
  });

const saveRecordUploadFailureLog = ({ recordType, recordUrl, error }) => {
  const logMessage = `[UPLOAD_RECORD failed] type=${recordType}, path=${recordUrl}`;

  console.error(logMessage, error);

  if (typeof window === "undefined") {
    return;
  }

  try {
    const previousLogs = JSON.parse(
      window.localStorage.getItem(RECORD_UPLOAD_FAILURE_LOG_KEY) || "[]",
    );

    previousLogs.push({
      message: logMessage,
      recordType,
      recordUrl,
      errorMessage: error?.message || String(error),
      createdAt: new Date().toISOString(),
    });

    window.localStorage.setItem(
      RECORD_UPLOAD_FAILURE_LOG_KEY,
      JSON.stringify(previousLogs.slice(-20)),
    );
  } catch (storageError) {
    console.error("Failed to persist record upload failure log", storageError);
  }
};

export const useRecord = () => {
  const userInfo = useSelector((state) => state.userInfo);
  const [loading, setLoading] = useState(false);

  const { screenStream } = useSelector((state) => state.device);
  const { myRoomId, myPeerId } = socketId(
    userInfo?.ROOM_IDX,
    userInfo?.Tester_IDX,
  );
  const siteId = process.env.NEXT_PUBLIC_SITE_ID || "VAC";
  const peerIdWithoutSitePrefix = myPeerId.startsWith(`${siteId}-`)
    ? myPeerId.slice(siteId.length + 1)
    : myPeerId;

  const registerUploadedRecord = async (recordType, recordUrl) => {
    let lastError;

    for (
      let attempt = 1;
      attempt <= RECORD_UPLOAD_REGISTER_RETRY_COUNT;
      attempt += 1
    ) {
      try {
        await axios.post(UPLOAD_RECORD, {
          recordType,
          recordUrl,
        });

        return;
      } catch (error) {
        lastError = error;

        if (attempt === RECORD_UPLOAD_REGISTER_RETRY_COUNT) {
          break;
        }

        console.warn(
          `UPLOAD_RECORD retry ${attempt}/${RECORD_UPLOAD_REGISTER_RETRY_COUNT} failed`,
          error,
        );
        await wait(RECORD_UPLOAD_REGISTER_RETRY_DELAY_MS);
      }
    }

    throw lastError;
  };

  const uploadRecordFile = async (fileName, blob, recordType) => {
    const file = new File([blob], fileName, { type: "video/webm" });

    const recordUrl = await uploadRecordFileWithFallback({
      file,
      fileName,
      peerId: myPeerId,
      roomId: myRoomId,
    });

    try {
      await registerUploadedRecord(recordType, recordUrl);
    } catch (error) {
      saveRecordUploadFailureLog({
        recordType,
        recordUrl,
        error,
      });
      throw error;
    }
  };

  /** 스크린 파일 저장*/
  const sendScreenWebCamFile = async () => {
    try {
      const timestamp = getRecordTimestamp();
      const fileName = `[${siteId}]${peerIdWithoutSitePrefix}_screen_${timestamp}.webm`;
      const blob = recorder_screen.getBlob();
      await uploadRecordFile(fileName, blob, "SCREEN");

      startScreenRecordingHandler(screenStream);
      setLoading(false);
    } catch {
      startScreenRecordingHandler(screenStream);
      setLoading("error");
    }
  };

  const startScreenRecordingHandler = async (screenStream) => {
    if (screenStream?.active) {
      const { default: RecordRTC } = await import("recordrtc");
      recorder_screen = await RecordRTC(screenStream, {
        type: "video",
        disableLogs: true,
      });

      await recorder_screen?.startRecording();
    }
  };

  /** 녹화 중지 */
  const stopRecordHandler = async () => {
    await recorder_screen?.stopRecording(sendScreenWebCamFile);
  };

  /** 녹화 시작 */
  const startRecord = async () => {
    if (recorder_screen) {
      await stopRecordHandler();
    } else {
      startScreenRecordingHandler(screenStream);
    }
  };

  return {
    startRecord,
    stopRecordHandler,
    loading,
    setLoading,
  };
};
